import BusinessIcon from '@mui/icons-material/Business';
import StoreMallDirectory from '@mui/icons-material/StoreMallDirectory';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import LoadingButton from '@mui/lab/LoadingButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import { styled, Theme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { FeatureFlag, SiteInfo } from '@cbo/shared-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { baseRoutes, siteLevelModifyRoutes } from '../../constants/routes';
import { useSites } from '../../contexts/siteContext';
import {
  getSiteButtonText,
  isAllSitesRoute,
  isExcludeSiteUpdateRoute,
  allSiteRouteWithRoles,
} from '../utils/siteUtils';
import SiteSelectorMenu from './SiteSelectorMenu';
import { appBarButtonSx, appBarButtonSxMobile } from '../../lib/styles/commonStyles';
import SiteSelectorDrawer from './SiteSelectorDrawer';
import SiteSelectorDialog from './SiteSelectorDialog';
import { useUsers } from '../../contexts/userContext';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';
import isPermitted from '../../lib/permissions';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import siteSort from '../../utils/siteUtils/siteSort';

export const organizationViewSite: SiteInfo = {
  enterpriseUnitId: '',
  name: 'Organization view',
  id: 'organizationView',
};

interface SiteSelectorProps {
  isOrgView: boolean;
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 150,
    textAlign: 'center',
  },
}));

const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;

function InternalSiteSelector({ isOrgView }: SiteSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const usagePerFeatureFlag = useFeatureFlag(FeatureFlag.usagePer);
  const isEuLevelRolesEnabled = useFeatureFlag(FeatureFlag.euLevelRoles);
  const user = useUsers();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { selectedSite, isSiteFiltering, multiSelectedSites, sortedSites, updateSelectedSite, lastSelectedSite } =
    useSites();
  const { isSingleSiteOrg } = useCurrentSite();
  const [isDisabled, setIsDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSiteSelectorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSiteSelection = useCallback(
    (site: SiteInfo) => {
      setDialogOpen(false);
      setAnchorEl(null);
      setDrawerOpen(false);

      if (site.enterpriseUnitId !== selectedSite.enterpriseUnitId) {
        const allSitesRoute = isAllSitesRoute(location.pathname) || isExcludeSiteUpdateRoute(location.pathname);
        const baseRoute = baseRoutes.find((path) => location.pathname.includes(path));

        // Check if the current route has a GUID, which will inevitably be site specific. If so
        // we want to route the user away so they won't encounter a 404
        // We don't want to do this on an all sites route because there is a guid in the URL for org level data
        // so routing back to the base route isn't needed.
        if (uuidRegex.test(window.location.href) && !allSitesRoute && baseRoute) {
          // The appended ?siteChange optional param will indicate that only this routing
          // event should trigger a site change if user accepts it with unsaved changes.
          const path = `${baseRoute}?siteChange`;
          // If the user has unsaved changes, this will track if they choose to cancel the
          // navigation or continue. If they continue, this will update the site automatically.
          const unregister = history.listen((locationListener) => {
            if (locationListener.pathname + locationListener.search === path) updateSelectedSite(site);

            // TBH this is a bit of a hack, to remove the appended ?siteChange
            window.history.replaceState(null, '', window.location.pathname);

            // This unregister is super important, because we only want to check the first route change
            // that happens after this function is called, and if that route change doesn't result in
            // the user going to that url we expect we don't want the site to change on the following route change
            unregister();
          });
          history.push(path);
        } else {
          updateSelectedSite(site);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, selectedSite.enterpriseUnitId, updateSelectedSite]
  );

  const handleViewAll = () => {
    setDialogOpen(true);
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  // This useEffect routes the user back to the last selected site when they leave an all sites route
  useEffect(() => {
    const allSitesRoute = isAllSitesRoute(location.pathname) || isExcludeSiteUpdateRoute(location.pathname);
    const currentSortedSites = user?.bslAuth ? siteSort(Object.values(user?.bslAuth?.sites)) : [];
    if (!allSitesRoute && selectedSite.name === organizationViewSite.name) {
      const site = lastSelectedSite.name === '' ? currentSortedSites[0] : lastSelectedSite;
      updateSelectedSite(site);
      return;
    }
    if (selectedSite.enterpriseUnitId === '' && selectedSite.id !== organizationViewSite.id) {
      const site = lastSelectedSite.name === '' ? currentSortedSites[0] : lastSelectedSite;
      updateSelectedSite(site);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]);

  useEffect(() => {
    const baseRoute = baseRoutes.find((path) => location.pathname.includes(path));
    const isSiteLevelModifyRoute = (usagePerFeatureFlag ? siteLevelModifyRoutes : []).some(
      (route) => !!matchPath(location.pathname, { path: route, exact: true })
    );
    if (isSiteFiltering || (baseRoute && uuidRegex.test(location.pathname)) || isSiteLevelModifyRoute) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [location.pathname, isSiteFiltering, usagePerFeatureFlag]);

  const topSites = useMemo(() => {
    const sites = sortedSites.slice(0, 7).map((option) => (
      <MenuItem
        onClick={() => handleSiteSelection(option)}
        key={option.enterpriseUnitId}
        selected={option.enterpriseUnitId === selectedSite?.enterpriseUnitId}
        sx={{ padding: '12px 8px 12px 12px', borderRadius: '8px', margin: '4px 12px' }}
      >
        <Typography variant='body1'>{option.name}</Typography>
      </MenuItem>
    ));
    // It helps to show org view to required user
    const allSiteRoute = allSiteRouteWithRoles.find((route) => location?.pathname?.includes(route.basePath));
    const hasOrgPermission = isEuLevelRolesEnabled ? allSiteRoute?.newRole : allSiteRoute?.oldRole;
    if (allSiteRoute && hasOrgPermission && isPermitted(user, hasOrgPermission, false) && !isSingleSiteOrg) {
      // it helps to show org view in starting of the list
      sites.unshift(
        <MenuItem
          key='view-all-sites'
          onClick={() => handleSiteSelection(organizationViewSite)}
          sx={{ padding: '12px 8px 12px 12px', borderRadius: '8px', margin: '4px 12px' }}
        >
          <Typography variant='body1'>{organizationViewSite.name}</Typography>
        </MenuItem>
      );
    }
    return sites;
  }, [
    handleSiteSelection,
    selectedSite?.enterpriseUnitId,
    sortedSites,
    location.pathname,
    isEuLevelRolesEnabled,
    user,
    isSingleSiteOrg,
  ]);

  const buttonText = !selectedSite.name?.length
    ? t('global.loading')
    : getSiteButtonText(
        selectedSite.name ?? '',
        multiSelectedSites,
        sortedSites.length,
        isSiteFiltering,
        true,
        t('globalFilters.sites'),
        t('globalFilters.allSites')
      );

  return isOrgView ? (
    <CustomTooltip arrow title={t('global.organizationViewTooltipText')}>
      <span>
        <Button disabled data-testid='org-view-tooltip-btn' startIcon={<StoreMallDirectory />} endIcon={<UnfoldMore />}>
          {t('navItems.organizationView')}
        </Button>
      </span>
    </CustomTooltip>
  ) : (
    <>
      <LoadingButton
        onClick={handleSiteSelectorClick}
        aria-label={buttonText}
        data-testid='site-selector-btn'
        startIcon={selectedSite?.enterpriseUnitId !== '' ? <StoreMallDirectory /> : <BusinessIcon />}
        endIcon={isMobile ? null : <UnfoldMore />}
        aria-haspopup='true'
        aria-expanded={anchorEl ? 'true' : undefined}
        disabled={isDisabled}
        sx={isMobile ? appBarButtonSxMobile : appBarButtonSx}
        loading={!selectedSite.name?.length}
        loadingPosition='start'
      >
        {isMobile ? null : buttonText}
      </LoadingButton>
      <SiteSelectorMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        handleViewAll={handleViewAll}
        topSites={topSites}
      />
      <SiteSelectorDrawer
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        topSites={topSites}
        handleViewAll={handleViewAll}
      />
      <SiteSelectorDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        handleSiteSelection={handleSiteSelection}
      />
    </>
  );
}

export default InternalSiteSelector;
